// `/waitlist` — one route, three audiences (ADR-0023).
//
// It is public because the confirmation and unsubscribe links in the waitlist
// email land here, and the person clicking them has no account. Sending them
// through the sign-in gate to read "your address is confirmed" would be absurd.
//
//   signed out          → the form, or the outcome of a confirm/remove link
//   signed in, waiting  → their position, and the join-code field
//   signed in, admitted → straight into the app; there is nothing to say

import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { WaitlistForm } from "../components/WaitlistForm";
import { Spinner } from "../components/Spinner";
import { redeemJoinCode } from "../lib/access";
import { useWorkspace } from "../lib/WorkspaceContext";
import { COMPANY } from "../lib/company";

export function Waitlist() {
  const { ready, access, refresh } = useWorkspace();
  const location = useLocation();
  const state = new URLSearchParams(location.search).get("state");

  const [code, setCode] = useState("");
  // Keyed, not a boolean: the ring belongs on the control that started the call,
  // and this screen will grow a second button.
  const [busy, setBusy] = useState<string | null>(null);
  const [rejected, setRejected] = useState(false);

  async function useCode() {
    setBusy("redeem");
    setRejected(false);
    try {
      if (await redeemJoinCode(code)) {
        await refresh();          // access state changes; the router follows it
      } else {
        setRejected(true);
      }
    } finally {
      setBusy(null);
    }
  }

  if (!ready) {
    return <main className="wrap"><p className="muted"><Spinner /> Loading…</p></main>;
  }

  // Admitted people have no business here.
  if (access.state === "allowed") return <Navigate to="/start" replace />;

  // Arrived from a link in the email.
  if (access.state === "anonymous" && state) {
    return (
      <main className="wrap wrap-narrow">
        <h1>{COMPANY.product}</h1>
        {state === "confirmed" && (
          <div className="card">
            <p>Your address is confirmed and your place is held.</p>
            <p className="muted small">We will email you when a place opens up.</p>
          </div>
        )}
        {state === "removed" && (
          <div className="card">
            <p>Removed. Your address is deleted and we will not write again.</p>
          </div>
        )}
        {state === "invalid" && (
          <>
            {/* Expired, wrong and already-used are one outcome on purpose. */}
            <div className="card err">That link is no longer valid.</div>
            <WaitlistForm source="confirm-retry" />
          </>
        )}
      </main>
    );
  }

  if (access.state === "anonymous") {
    return (
      <main className="wrap wrap-narrow">
        <h1>{COMPANY.product} is invitation-only for now</h1>
        <p className="muted">
          We are opening the product to a few accounts at a time so that every one of
          them gets a real answer when something goes wrong. Leave your address and we
          will tell you when a place opens up.
        </p>
        <WaitlistForm source="waitlist-page" />
      </main>
    );
  }

  // Signed in and waiting.
  return (
    <main className="wrap wrap-narrow">
      <h1>You are on the list</h1>

      {access.waitlistPosition != null ? (
        <p>You are number <strong>{access.waitlistPosition}</strong> in the queue.</p>
      ) : access.waitlistStatus === "pending" ? (
        <p>Confirm your address from the email we sent, and your place is held.</p>
      ) : (
        <p>We have your address and will email you when a place opens up.</p>
      )}

      <div className="card">
        <label htmlFor="join-code">Have a join code?</label>
        <input
          id="join-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          className="primary"
          onClick={useCode}
          disabled={busy === "redeem" || code.trim() === ""}
        >
          {busy === "redeem" ? <Spinner label="Checking the code" /> : "Use code"}
        </button>
        {/* One message for every reason: wrong, expired, revoked, used up. A
            field that distinguishes them is a field for guessing against. */}
        {rejected && <p className="err">That code is not valid.</p>}
      </div>

      <p className="muted small">
        Questions? <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a>
      </p>
    </main>
  );
}
